import type { ContextType } from "react";
import { createContext, useCallback, useContext } from "react";
import { useFetcher } from "react-router";
import type { FetcherWithComponents } from "react-router";

import { useURL } from "~/contexts";
import type { C_ProductInfo } from "~/lib/app.types";

import type { action } from "../save-for-later";
import { RESOURCE_URL, SaveForLaterSchema } from "../schemas/save-for-later";
import type { Actions } from "../schemas/save-for-later";

/**
 * Pull the in-flight productId off a fetcher's `formData`. Parses via the
 * canonical Zod schema so callers get a typed `string` (not the raw
 * `FormDataEntryValue` union).
 */
const pendingProductId = (
  fetcher: FetcherWithComponents<unknown>,
): string | null => {
  if (fetcher.state === "idle" || !fetcher.formData) return null;
  const parsed = SaveForLaterSchema.safeParse(
    Object.fromEntries(fetcher.formData),
  );
  return parsed.success ? parsed.data.productId : null;
};

interface SaveForLaterContext {
  /** Product IDs the customer (or guest) has saved for later. */
  productIds: string[];
  /** Expanded product data for the saved IDs. */
  products: C_ProductInfo[];
  /**
   * Guest: membership in the localStorage list (valid on every route).
   * Logged-in: membership in the SERVER list, which only loads on /cart — so
   * it is cart-only there and reads `false` elsewhere.
   */
  isInList: (productId: string) => boolean;
  /**
   * True once the provider's cart-route load has SETTLED — success or
   * contained failure (see `degraded`). Off the cart route the load never
   * fires, so this stays false there; no consumer off /cart reads it.
   *
   * COM-63: `isLoggedIn` was removed from this context. It used to be
   * re-derived from the SFL fetch, which now only runs on /cart — every
   * other reader would have seen "guest" and written a logged-in shopper's
   * save to localStorage. Read auth from `useAuth()` instead.
   */
  hydrated: boolean;
  /**
   * COM-63 — a cart-route load is IN FLIGHT right now (initial, or a refetch
   * after the guest id list / auth changed). `hydrated` is sticky across
   * refetches, so this is what a consumer holds a skeleton on when a saved id
   * has no product data yet.
   */
  loading: boolean;
  /**
   * COM-63 — the last SFL load FAILED (contained; WAF 403 / 5xx / network),
   * or a guest's localStorage is blocked, so an empty `productIds` means
   * "couldn't load", not "no saved items". Consumers must not render a
   * confident-empty state while this is true.
   */
  degraded: boolean;
  /**
   * Guest-only mutators. Auth users mutate via `useSaveForLaterActions`
   * (server action route). For guests these write localStorage and update
   * the provider's reactive snapshot in one step — no custom DOM events.
   * Calling these as an authenticated user is a no-op (returns `true`).
   * Return `false` when the localStorage write failed (COM-63 P2-9b) —
   * callers must not claim success on a `false`.
   */
  guestAdd: (productId: string) => boolean;
  guestRemove: (productId: string) => boolean;
  /**
   * Auth-only optimistic hide. Filters productId from `productIds` /
   * `products` immediately so SFL → Cart move doesn't flash a stale row
   * during the server-side `cleanupAfterMove` round-trip. Provider reconciles
   * on the next loader response. Guest path uses `guestRemove` (sync
   * localStorage write) so no optimistic layer needed there.
   */
  optimisticRemove: (productId: string) => void;
}

const SaveForLaterContext = createContext<SaveForLaterContext | undefined>(
  undefined,
);

export const useSaveForLater = (): ContextType<typeof SaveForLaterContext> => {
  return useContext(SaveForLaterContext);
};

/**
 * Separate fetchers per action so rapid `add → remove` clicks don't cancel
 * each other (single shared fetcher cancels prior submission on re-submit).
 * `cleanupFetcher` submits `action: "remove"` after a SFL → basket move so
 * the regular `removeFetcher` settle observer (which toasts) doesn't fire on
 * move-cleanup. Same server handler, different fetcher key.
 */
export const useSaveForLaterActions = () => {
  const addFetcher = useFetcher<typeof action>({ key: "sfl-add" });
  const removeFetcher = useFetcher<typeof action>({ key: "sfl-remove" });
  const cleanupFetcher = useFetcher<typeof action>({ key: "sfl-move-cleanup" });
  const url = useURL();

  const submit = useCallback(
    (
      fetcher:
        | ReturnType<typeof useFetcher<typeof action>>
        | ReturnType<typeof useFetcher<typeof action>>,
      payload: Actions,
    ) => {
      fetcher.submit(payload, { action: url(RESOURCE_URL), method: "post" });
    },
    [url],
  );

  const add = useCallback(
    (productId: string) => submit(addFetcher, { action: "add", productId }),
    [addFetcher, submit],
  );
  const remove = useCallback(
    (productId: string) =>
      submit(removeFetcher, { action: "remove", productId }),
    [removeFetcher, submit],
  );
  const cleanupAfterMove = useCallback(
    (productId: string) =>
      submit(cleanupFetcher, { action: "remove", productId }),
    [cleanupFetcher, submit],
  );
  /**
   * Atomic cart-line → SFL (auth users only — guests use localStorage path).
   */
  const saveLineItem = useCallback(
    (productId: string, itemId: string) =>
      submit(addFetcher, { action: "saveLineItem", productId, itemId }),
    [addFetcher, submit],
  );

  const isPending = (productId: string): boolean =>
    [addFetcher, removeFetcher, cleanupFetcher].some(
      f => pendingProductId(f) === productId,
    );

  return {
    add,
    remove,
    cleanupAfterMove,
    saveLineItem,
    addFetcher,
    removeFetcher,
    cleanupFetcher,
    isPending,
  };
};

export { SaveForLaterContext };
